import CustomInput from '../../../reusable/input';
import CustomModal from '../../../modals';
import { notesSchema } from '../../../../schema/notes_schema';
import { settingSelectedNote } from '../../../../redux/slices/note';
import { addDealNoteThunk, editDealNoteThunk } from '../../../../redux/thunks/note';
import { AppDispatch, RootState } from '../../../../redux/store';
import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: NoteRender Component
 * @paragraph: A React functional component to render and manage notes related to a deal. It allows users to add, view, and delete notes.
 * @interface: NoteInterface
 * @property: {string} notes - The content of the note to be added.
 */
interface AddNoteModalProps {
    open: boolean;
    onClose: () => void;
}
/**
 * @heading2: NoteRender
 */
const AddNoteModal = ({ open, onClose }: AddNoteModalProps) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * initialValues: {NoteInterface} - Initial values for adding note.
     * selectedDeal/notes/currentUser: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { loaders, selectedNote } = useSelector((state: RootState) => state.note);
    const { selectedDeal } = useSelector((state: RootState) => state.deal);
    const initialValues = {
        notes: selectedNote?.description || ''
    };

    /**
     * @heading3: Functions
     * @heading4: handleSubmit
     * @paragraph: Handles the submission of the note form.
     * @param: {NoteInterface} values - The form values containing the note content.
     */
    const onCloseHandler = () => {
        dispatch(settingSelectedNote(null));
        onClose();
    };
    const handleSubmit = async (values: {
        notes: string;
    }) => {
        try {
            if (selectedNote) {
                await dispatch(editDealNoteThunk({
                    deal_id: selectedDeal?.id as number,
                    note_id: selectedNote.id,
                    note: values.notes
                })).unwrap();
            } else {
                await dispatch(addDealNoteThunk({
                    deal_id: selectedDeal?.id as number,
                    note: values.notes
                })).unwrap();
            }
            onCloseHandler();
        } catch (e) {
            // Catch errors
        }
    };

    const disabled = loaders.isCreating || loaders.isUpdating;

    return (
        <CustomModal open={open} handleClose={disabled ? () => null : onCloseHandler} title={selectedNote ? 'Edit Note' : 'Add note'}>
            <Formik
                initialValues={initialValues}
                validationSchema={notesSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, touched, errors }) => (
                    <Form>
                        <Box width="100%" height="30vh" >
                            <CustomInput
                                type="quill"
                                value={values.notes}
                                name="notes"
                                placeholder="Type content here..."
                                disabled={disabled}
                                quillHeight="22vh"
                                error={Boolean(errors.notes && touched.notes)}
                                onChangeForPhone={(value: string) => setFieldValue('notes', value)}
                            />
                            {/* <CustomToolbar />
                            <ReactQuill
                                onChange={(content) => setFieldValue('notes', content)}
                                value={values.notes}
                                placeholder="Type content here..."
                                modules={quillModule}
                                formats={quillFormats}
                                theme="snow"
                                readOnly={disabled}
                                style={{
                                    height: '22vh',
                                    borderBottomLeftRadius: '10px',
                                    borderBottomRightRadius: '10px',
                                     border:  ? `1px solid ${red[300]}` : 'none'
                                }}
                            /> */}
                        </Box>
                        {/* Action button */}
                        <Box display="flex" alignItems="center" gap={2} mt={3}>
                            <Button variant="outlined" color="secondary" onClick={onCloseHandler} fullWidth disabled={disabled}>Cancel</Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={disabled}>
                                {disabled ? 'Saving...' : 'Save'}
                            </Button></Box>
                    </Form>)}
            </Formik>
        </CustomModal>
    );
};

export default AddNoteModal;